/**
 * Work that rewrites the whole batch (a re-read, applying or undoing a job improvement) waits its turn
 * instead of being refused. Before this queue, a click that met a page turn, a field save or pages still
 * arriving answered "Finish X before Y" and did nothing, so the operator had to watch for the other work
 * to end and click again.
 *
 * The claim stays the batch's single admission (claimWholeBatchMutation in App.tsx): the queue only
 * retries it whenever a save lane opens or closes, and gives up when the wait stops being wanted (the
 * window closed, or the batch it was asked for is gone). One wait per key: a second click on the same
 * thing joins the first rather than queueing it twice.
 */
export type WholeBatchTurn = {
  /** Claims the whole batch, or null while other work holds a lane. */
  claim: () => symbol | null;
  release: (token: symbol) => void;
  /** Resolves when a save lane may have changed. */
  nextChange: () => Promise<void>;
  /** False once the wait should be dropped. */
  stillWanted: () => boolean;
};

export class WholeBatchQueue {
  private readonly pending = new Map<string, Promise<unknown>>();

  /** Whether work under this key is waiting or running. */
  has(key: string) {
    return this.pending.has(key);
  }

  /**
   * Runs `work` holding the batch: at once when it is free, else after the work ahead of it. `onWaiting`
   * runs once, only when the work has to wait. Resolves undefined when the wait was dropped.
   */
  run<T>(
    key: string,
    turn: WholeBatchTurn,
    onWaiting: () => void,
    work: (token: symbol) => Promise<T>,
  ): Promise<T | undefined> {
    const joined = this.pending.get(key);
    if (joined) return joined as Promise<T | undefined>;
    const first = turn.claim();
    if (!first) onWaiting();
    const running = (async () => {
      let token = first;
      while (!token) {
        await turn.nextChange();
        if (!turn.stillWanted()) return undefined;
        token = turn.claim();
      }
      try {
        return await work(token);
      } finally {
        turn.release(token);
      }
    })().finally(() => this.pending.delete(key));
    this.pending.set(key, running);
    return running;
  }
}
